import { useState, useEffect } from 'react';
import Swal from 'sweetalert2';
import {
    TextField,
    Box,
    Backdrop,
    Stack,
} from '@mui/material';

import CustomButton from "../custom_components/CustomButton";
import CustomModalHeader from "../custom_components/CustomModalHeader";

import { getNewListId } from '../../utils/getNewListId';
import { getPhilippinesDate } from '../../utils/getPhilippinesDate';

const apiUrl = process.env.REACT_APP_API_URL;

const CreateList = ({ open, onClose, onUpdate }) => {

    const [listId, setListId] = useState("");
    const [listName, setListName] = useState("");
    const [date, setDate] = useState(getPhilippinesDate());

    const [isSaving, setIsSaving] = useState(false);

    // INITIALIZED
    useEffect(() => {
        if (!open) return;

        let cancelled = false;

        const initialize = async () => {
            setListId("");
            setListName("");
            setDate(getPhilippinesDate());

            const newId = await getNewListId();

            if (!cancelled) {
                setListId(newId);
            }
        };

        initialize();

        return () => {
            cancelled = true;
        };
    }, [open]);

    // SAVE NEW LIST
    const handleSaveList = async () => {
        if (!listId || !listName || !date) {
            Swal.fire({ text: "Please fill all the required inputs.", icon: "warning" });
            return;
        }

        setIsSaving(true);

        try {
            const response = await fetch(
                `${apiUrl}manage_list/create_list.php`,
                {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        listId: listId,
                        listName: listName,
                        listDate: date,
                    })
                });

            const result = await response.json();

            if (result.status === 'success') {

                onUpdate({
                    DateCreated: date,
                    ListID: listId,
                    ListName: listName,
                    Remarks: "New",
                    TotalItems: 0,
                    TotalTransit: "",
                });

                handleClose();

                Swal.fire({
                    icon: "success",
                    text: result.message,
                    timer: 3500,
                    showConfirmButton: false,
                });

            } else if (result.status === 'error') {
                Swal.fire({
                    text: result.message,
                    icon: "error"
                });
            }
        } catch (error) {
            Swal.fire({
                title: 'Error saving list!',
                text: error,
                icon: "error"
            });
            console.error('Error saving list:', error);
        } finally {
            setIsSaving(false);
        }
    };

    const handleClose = async () => {
        setListId("");
        setListName("");
        setDate(getPhilippinesDate());
        onClose();
    }

    return (
        <Backdrop
            aria-hidden="false"
            open={open}
            sx={{
                zIndex: (theme) => theme.zIndex.drawer + 1,
                backdropFilter: "blur(1px)",
            }}
        >
            <Box
                sx={{
                    width: 420,
                    backgroundColor: "#ffffff",
                    borderRadius: 2,
                    p: 2.5,
                    boxShadow: 6,

                    position: "relative",
                    overflow: "hidden",
                    "&::before": {
                        content: '""',
                        position: "absolute",
                        top: 0,
                        left: 0,
                        width: "100%",
                        height: "4px",
                        background: "#2e3489"
                    }
                }}
            >

                {/* HEADER */}
                <CustomModalHeader
                    title="Create List"
                    subtitle={`ID: ${listId || "Generating..."}`}
                    onClose={handleClose}
                />

                {/* FORM */}
                <Stack spacing={2.2}>
                    <TextField
                        label="List Name"
                        size="small"
                        fullWidth
                        value={listName}
                        onChange={(e) => setListName(e.target.value)}
                    />

                    <TextField
                        label="Date"
                        type="date"
                        size="small"
                        fullWidth
                        value={date}
                        onChange={(e) => setDate(e.target.value)}
                    />
                </Stack>

                {/* ACTIONS */}
                <Stack
                    direction="row"
                    justifyContent="flex-end"
                    spacing={2}
                    mt={4}
                >

                    <CustomButton
                        label="Cancel"
                        variant="outlined"
                        hoverColor="#d14249"
                        onClick={handleClose}
                    />

                    <CustomButton
                        label={isSaving ? "Saving..." : "Save List"}
                        disabled={isSaving || !listId}
                        onClick={handleSaveList}
                    />

                </Stack>

            </Box>

        </Backdrop >
    );
};

export default CreateList;
